import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Modal, Input, Select } from "barte-design-system";
import { BarcodeIcon } from "@phosphor-icons/react";
import { usePostHog } from '@posthog/react';

import { InputNumber } from '@/modules/shared/components'
import { useValidationErrors } from "@payfac/hooks";
import { TRACK_EVENTS } from '@payfac/constants';

import { objectShape } from "./modal-edit-boleto.constants";

import type { ModalEditBoletoProps } from "./modal-edit-boleto.type";
import styles from "./modal-edit-boleto.module.css";
import { formatCurrency, formatPercentage } from "@shared/helpers";

export const ModalEditBoleto = ({
    isOpen,
    onClose,
    data,
    onHandleSubmit,
    isLoading,
    companyRate,
    companyRateType,
}: ModalEditBoletoProps) => {
    const { t } = useTranslation();
    const posthog = usePostHog();

    const [dataForm, setDataForm] = useState(data);
    const [initEdit, setInitEdit] = useState(false);

    const { isValid, errors, onHandleListenErrors } = useValidationErrors(objectShape(t), dataForm);

    const onChangeValues = (key: keyof ModalEditBoletoProps['data'], value: ModalEditBoletoProps['data'][keyof ModalEditBoletoProps['data']]) => {

        const updatedObject = { ...dataForm, [key]: value } as ModalEditBoletoProps['data'];

        if (key === "rateType" && value === "FIXED") {
            delete updatedObject?.valueLimit;
        }

        setDataForm(updatedObject);

        if (!initEdit) {
            setInitEdit(true);
        }
    };

    const handleSave = () => {
        if (!isValid) {
            onHandleListenErrors(true);
            return;
        }

        onHandleSubmit(dataForm);
        posthog.capture(TRACK_EVENTS.RATE_PLAN.BOLETO_CLICKED);
    };

    const title = <div className={styles["modal-header"]}><BarcodeIcon /> {t("payfac.pageRatePlanModalEditBoletoTitle")}</div> as unknown as string
    const hasDiff = JSON.stringify(dataForm) !== JSON.stringify(data);

    useEffect(() => {
        if (isOpen) {
            if (!initEdit) {
                setDataForm(data);
            }
        } else {
            onHandleListenErrors(false);
            setDataForm({} as ModalEditBoletoProps['data']);
            setInitEdit(false);
        }
    }, [isOpen, data, initEdit, onHandleListenErrors]);

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={title}
            size="lg"
            footer={{
                primaryButton: {
                    children: t("payfac.pageRatePlanModalSaveButton"),
                    onClick: handleSave,
                    disabled: !hasDiff,
                    loading: isLoading,
                },
                secondaryButton: {
                    children: t("payfac.pageRatePlanModalCancelButton"),
                    variant: "outline",
                    onClick: onClose,
                    disabled: isLoading,
                },
                buttonLayout: "hug",
            }}
        >
            <div className={styles.content}>
                <div className={styles.field}>
                    <InputNumber
                        label={t("payfac.pageRatePlanModalRepassTimeLabel")}
                        value={dataForm?.delayForRedeem}
                        onChange={(value) => onChangeValues("delayForRedeem", value)}
                        prefix="D + "
                        decimalPrecision={0}
                        error={errors?.delayForRedeem}
                        helperText={errors?.delayForRedeem || ''}
                    />
                </div>

                <div className={styles.field}>
                    <InputNumber
                        label={t("payfac.pageRatePlanModalTotalChargedLabel")}
                        value={dataForm?.value}
                        onChange={(value) => onChangeValues("value", value)}
                        error={errors?.value}
                        helperText={errors?.value || ''}
                    />
                </div>

                <div className={styles.field}>
                    <Select
                        label={t("payfac.pageRatePlanModalRateTypeLabel")}
                        options={[
                            { value: "PERCENTAGE", label: t("payfac.pageRatePlanDetailsPercentage") },
                            { value: "FIXED", label: t("payfac.pageRatePlanDetailsFixed") },
                        ]}
                        value={dataForm?.rateType || "PERCENTAGE"}
                        onChange={(value) => onChangeValues("rateType", value)}
                        hidePrefixIcon
                    />
                </div>

                {dataForm?.rateType === "PERCENTAGE" && (
                    <div className={styles.field}>
                        <InputNumber
                            label={t("payfac.pageRatePlanModalRateLimitLabel")}
                            value={dataForm?.valueLimit}
                            onChange={(value) => onChangeValues("valueLimit", value)}
                            prefix="R$ "
                            error={errors?.valueLimit}
                            helperText={errors?.valueLimit || ''}
                        />
                    </div>
                )}

                <div className={styles.field}>
                    <Input
                        label={t("payfac.pageRatePlanModalBarteRateLabel")}
                        value={companyRateType === "PERCENTAGE" ? formatPercentage(companyRate || 0) : formatCurrency(companyRate || 0)}
                        onChange={() => null}
                        disabled
                    />
                </div>
            </div>
        </Modal>
    );
};
